import { useLazyQuery, useMutation } from '@apollo/client';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
// material
import {
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    Container,
    InputAdornment,
    Menu,
    MenuItem,
    OutlinedInput,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { GET_ALL_MERCHANTS, SWITCH_MERCHANT } from '../../../_apis_/queries/merchant';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { getMerchantsSuccess } from '../../../redux/slices/merchant';
import { setRowsPerPage, setSelectedExcludeServices, setSelectedIncludeServices } from '../../../redux/slices/merchantList';

// @types
// components
import { MerchantListHead } from '../../../components/_dashboard/merchant/list';
import Page from '../../../components/Page';
import Scrollbar from '../../../components/Scrollbar';
// hooks
import { styled } from '@mui/material/styles';
import useSettings from '../../../hooks/useSettings';
// redux
import { RootState, useDispatch, useSelector } from '../../../redux/store';
// routes
import { AppService, MerchantStatus } from '@/__generated__/types';
import { GetAllMerchantsQuery } from '@/__generated__/types';
import searchFill from '@iconify/icons-eva/search-fill';
import clear from '@iconify/icons-ic/clear';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../../components/commonComponents/ConfirmDialog';
import LoadingComponent from '../../../components/LoadingComponent';
import SearchNotFound from '../../../components/SearchNotFound';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { applySortFilter, getComparator } from '../../../utils/functions';

// ----------------------------------------------------------------------

enum FilterType {
    INCLUDE = 'include',
    EXCLUDE = 'exclude'
}

const TABLE_HEAD = [
    { id: 'name', label: 'Name', alignRight: false },
    { id: 'altName', label: 'Alt Name', alignRight: false },
    { id: 'merchantId', label: 'merchantId', alignRight: false },
    { id: 'posMerchantId', label: 'POS MerchantId', alignRight: false },
    { id: 'merchantStatus', label: 'Status', alignRight: false },
    { id: 'appServices', label: 'Services', alignRight: false }
];

const LOYALTY_STRING = 'loyalty';

// ----------------------------------------------------------------------
export const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
    width: 300,
    height: 56,
    transition: theme.transitions.create(['box-shadow', 'width'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shorter
    }),
    '&.Mui-focused': { boxShadow: theme.customShadows.z8 },
    '& fieldset': {
        borderWidth: `1px !important`,
        borderColor: `${theme.palette.grey[500_32]} !important`
    }
}));

export default function MerchantList(): JSX.Element {
    const authMerchantId = useSelector((state: RootState) => state.auth.user?.merchantId || null);
    const { themeStretch } = useSettings();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const { selectedIncludeServices, selectedExcludeServices, rowsPerPage } = useSelector((state: RootState) => state.merchantList);
    const [includeMenuAnchor, setIncludeMenuAnchor] = useState<null | HTMLElement>(null);
    const [excludeMenuAnchor, setExcludeMenuAnchor] = useState<null | HTMLElement>(null);

    const navigate = useNavigate();
    const [dataMerchant] = useLazyQuery(GET_ALL_MERCHANTS, {
        onCompleted(response) {
            onGetAllMerchantCompleted(response);
        },
        onError() {
            setIsLoading(false);
        },
        fetchPolicy: 'network-only'
    });
    const onGetAllMerchantCompleted = async (response: GetAllMerchantsQuery): Promise<void> => {
        if (response.getAllMerchants && response.getAllMerchants.length > 0) {
            dispatch(getMerchantsSuccess(response.getAllMerchants.filter(Boolean).filter((merchant) => merchant !== null)));
        }
        setIsLoading(false);
    };
    const getData = (): void => {
        setIsLoading(true);
        dataMerchant();
    };
    const merchantList = useSelector((state: RootState) => state.merchant.merchantList);
    useEffect(() => {
        getData();
    }, []);
    const [filteredMerchantList, setFilteredMerchantList] = useState(merchantList);
    const { enqueueSnackbar } = useSnackbar();
    const [isSwitchMerchant, setIsSwitchMerchant] = useState(false);
    const [merchantId, setMerchantId] = useState('');
    const [page, setPage] = useState(0);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('name');
    const handleRequestSort = (property: string): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
        setPage(0);
    };

    const handleIncludeMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setIncludeMenuAnchor(event.currentTarget);
    };

    const handleIncludeMenuClose = (): void => {
        setIncludeMenuAnchor(null);
    };

    const handleExcludeMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setExcludeMenuAnchor(event.currentTarget);
    };

    const handleExcludeMenuClose = (): void => {
        setExcludeMenuAnchor(null);
    };

    const handleServiceToggle = (service: string, filterType: FilterType): void => {
        if (filterType === FilterType.INCLUDE) {
            const newServices = selectedIncludeServices.includes(service)
                ? selectedIncludeServices.filter((s) => s !== service)
                : [...selectedIncludeServices, service];
            dispatch(setSelectedIncludeServices(newServices));
        } else {
            const newServices = selectedExcludeServices.includes(service)
                ? selectedExcludeServices.filter((s) => s !== service)
                : [...selectedExcludeServices, service];
            dispatch(setSelectedExcludeServices(newServices));
        }
    };

    const handleClearFilter = (): void => {
        dispatch(setSelectedIncludeServices([]));
        dispatch(setSelectedExcludeServices([]));
    };

    const [switchMerchantMutation] = useMutation(SWITCH_MERCHANT, {
        onCompleted() {
            enqueueSnackbar(t('Switch Merchant Success'), { variant: 'success' });
        },
        onError(error) {
            enqueueSnackbar(`${t('Switch Merchant Fail')}: ${error.message}`, { variant: 'error' });
        }
    });

    const handleMerchantNameOnClick = (merchantId: string): void => {
        const path = `${PATH_DASHBOARD.merchant.root}/${merchantId}/edit`;
        // 使用navigate进行跳转
        navigate(path);
    };

    const handleMerchantIdOnClick = (merchantId: string): void => {
        setMerchantId(merchantId);
        setIsSwitchMerchant(true);
    };

    const handleSwitchMerchant = async (): Promise<void> => {
        const variables = {
            merchantId: merchantId
        };
        await switchMerchantMutation({ variables });
        await setIsSwitchMerchant(false);
        window.location.reload();
    };

    const canSwitchMerchant = (merchantId: string): boolean => {
        if (authMerchantId === merchantId) {
            return false;
        }
        return true;
    };

    const getMerchantStatusColor = (status: MerchantStatus): 'success' | 'error' | 'inherit' => {
        switch (status) {
            case MerchantStatus.Active:
                return 'success';
            case MerchantStatus.Inactive:
                return 'error';
            default:
                return 'inherit';
        }
    };

    useEffect(() => {
        const sortedMerchantList = applySortFilter(merchantList, getComparator(order, orderBy), '');
        const searchedMerchant = sortedMerchantList.filter((merchant) => {
            const matchesSearch =
                merchant.merchantId?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.phoneNumber?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.altName?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.abn?.toLowerCase().includes(searchValue.toLowerCase()) ||
                merchant.merchantStatus?.toLowerCase().includes(searchValue.toLowerCase()) ||
                (merchant.merchantAdminEmail && merchant.merchantAdminEmail?.toLowerCase().includes(searchValue.toLowerCase())) ||
                merchant.type?.toLowerCase().includes(searchValue.toLowerCase());

            // Get merchant services
            const merchantServices: string[] = [];
            if (merchant.settings?.appServices && Array.isArray(merchant.settings.appServices)) {
                merchant.settings.appServices.forEach((service) => {
                    if (service) {
                        merchantServices.push(service);
                    }
                });
            }
            if (merchant.loyaltyProgram) {
                merchantServices.push(LOYALTY_STRING);
            }

            // If no filters applied, show all merchants
            if (selectedIncludeServices.length === 0 && selectedExcludeServices.length === 0) {
                return matchesSearch;
            }

            // Apply include filter: merchant must have all of the selected include services
            const matchesIncludeFilter =
                selectedIncludeServices.length === 0 || selectedIncludeServices.every((service) => merchantServices.includes(service));

            // Apply exclude filter: merchant must NOT have selected exclude services
            const matchesExcludeFilter =
                selectedExcludeServices.length === 0 || !selectedExcludeServices.some((service) => merchantServices.includes(service));

            return matchesSearch && matchesIncludeFilter && matchesExcludeFilter;
        });
        setFilteredMerchantList(searchedMerchant);
    }, [searchValue, selectedIncludeServices, selectedExcludeServices, merchantList, order, orderBy]);

    const isMerchantNotFound = filteredMerchantList?.length === 0;
    const isNoItem = merchantList?.length === 0;

    return (
        <Page title={t('Merchant List')}>
            <Container maxWidth={themeStretch ? false : 'xl'}>
                <HeaderBreadcrumbs
                    heading={t('Merchant List')}
                    links={[
                        { name: t('Dash Board'), href: PATH_DASHBOARD.root },
                        { name: t('Merchant'), href: PATH_DASHBOARD.merchant.list },
                        { name: t('List') }
                    ]}
                    action={
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to={PATH_DASHBOARD.merchant.newMerchant}
                            startIcon={<Icon icon={plusFill} />}
                        >
                            {t('New Merchant')}
                        </Button>
                    }
                />
                <Box
                    component="form"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                    noValidate
                    autoComplete="off"
                >
                    <SearchStyle
                        placeholder={t('Search merchant...')}
                        value={searchValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchValue(e.target.value);
                        }}
                        startAdornment={
                            <InputAdornment position="start">
                                <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        }
                        endAdornment={
                            <InputAdornment position="end">
                                <Box
                                    component={Icon}
                                    icon={clear}
                                    sx={{ color: 'text.disabled', cursor: 'pointer' }}
                                    onClick={() => setSearchValue('')}
                                />
                            </InputAdornment>
                        }
                    />

                    {/* Include Services Filter */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: 56, px: 1 }}>
                        <Tooltip title={t('Include: Show merchants with these services')}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 0.75,
                                    bgcolor: 'success.lighter',
                                    borderRadius: 1,
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: 'success.dark',
                                    flexShrink: 0
                                }}
                            >
                                {t('INCLUDE')}
                            </Box>
                        </Tooltip>
                        <Button variant="outlined" color="success" onClick={handleIncludeMenuOpen} sx={{ textTransform: 'none' }}>
                            {selectedIncludeServices.length > 0
                                ? `${selectedIncludeServices.length} ${t('selected')}`
                                : t('Select services')}
                        </Button>
                        <Menu anchorEl={includeMenuAnchor} open={Boolean(includeMenuAnchor)} onClose={handleIncludeMenuClose}>
                            {[
                                { value: AppService.Booking, label: 'Booking' },
                                { value: AppService.Delivery, label: 'Delivery' },
                                { value: AppService.Dinein, label: 'Dine In' },
                                { value: AppService.OnlineMenu, label: 'Online Menu' },
                                { value: AppService.Pickup, label: 'Pickup' },
                                { value: LOYALTY_STRING, label: 'Loyalty' }
                            ].map((service) => (
                                <MenuItem
                                    key={`include-menu-${service.value}`}
                                    onClick={() => handleServiceToggle(service.value, FilterType.INCLUDE)}
                                >
                                    <Checkbox checked={selectedIncludeServices.includes(service.value)} sx={{ mr: 1 }} />
                                    {t(service.label)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {selectedIncludeServices.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {selectedIncludeServices.map((service) => (
                                    <Chip
                                        key={`include-chip-${service}`}
                                        label={service === LOYALTY_STRING ? t('Loyalty') : t(service)}
                                        onDelete={() => handleServiceToggle(service, FilterType.INCLUDE)}
                                        color="success"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Exclude Services Filter */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: 56, px: 1 }}>
                        <Tooltip title={t('Exclude: Hide merchants with these services')}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 0.75,
                                    bgcolor: 'error.lighter',
                                    borderRadius: 1,
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: 'error.dark',
                                    flexShrink: 0
                                }}
                            >
                                {t('EXCLUDE')}
                            </Box>
                        </Tooltip>
                        <Button variant="outlined" color="error" onClick={handleExcludeMenuOpen} sx={{ textTransform: 'none' }}>
                            {selectedExcludeServices.length > 0
                                ? `${selectedExcludeServices.length} ${t('selected')}`
                                : t('Select services')}
                        </Button>
                        <Menu anchorEl={excludeMenuAnchor} open={Boolean(excludeMenuAnchor)} onClose={handleExcludeMenuClose}>
                            {[
                                { value: AppService.Booking, label: 'Booking' },
                                { value: AppService.Delivery, label: 'Delivery' },
                                { value: AppService.Dinein, label: 'Dine In' },
                                { value: AppService.OnlineMenu, label: 'Online Menu' },
                                { value: AppService.Pickup, label: 'Pickup' },
                                { value: LOYALTY_STRING, label: 'Loyalty' }
                            ].map((service) => (
                                <MenuItem
                                    key={`exclude-menu-${service.value}`}
                                    onClick={() => handleServiceToggle(service.value, FilterType.EXCLUDE)}
                                >
                                    <Checkbox checked={selectedExcludeServices.includes(service.value)} sx={{ mr: 1 }} />
                                    {t(service.label)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {selectedExcludeServices.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {selectedExcludeServices.map((service) => (
                                    <Chip
                                        key={`exclude-chip-${service}`}
                                        label={service === LOYALTY_STRING ? t('Loyalty') : t(service)}
                                        onDelete={() => handleServiceToggle(service, FilterType.EXCLUDE)}
                                        color="error"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {(selectedIncludeServices.length > 0 || selectedExcludeServices.length > 0) && (
                        <Tooltip title={t('Clear all filters')}>
                            <Button size="small" variant="text" onClick={handleClearFilter}>
                                {t('Clear')}
                            </Button>
                        </Tooltip>
                    )}
                </Box>
                <Card>
                    <Scrollbar>
                        <TableContainer sx={{ minWidth: 800 }}>
                            <Table>
                                <MerchantListHead
                                    order={order}
                                    orderBy={orderBy}
                                    headLabel={TABLE_HEAD}
                                    rowCount={merchantList.length}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow style={{ height: 53 }}>
                                            <TableCell colSpan={8}>
                                                <LoadingComponent />
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredMerchantList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                            const { merchantId, name, orderbuddyMerchantId, altName, merchantStatus, settings } = row;
                                            return (
                                                <TableRow hover key={merchantId} tabIndex={-1} role="checkbox">
                                                    <TableCell
                                                        onClick={() => handleMerchantNameOnClick(merchantId)}
                                                        align="left"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                textDecoration: 'underline'
                                                            }}
                                                        >
                                                            {name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="left">{altName}</TableCell>
                                                    <TableCell
                                                        onClick={() => canSwitchMerchant(merchantId) && handleMerchantIdOnClick(merchantId)}
                                                        style={{
                                                            cursor: canSwitchMerchant(merchantId) ? 'pointer' : 'default'
                                                        }}
                                                        align="left"
                                                    >
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                textDecoration: canSwitchMerchant(merchantId) ? 'underline' : 'none'
                                                            }}
                                                        >
                                                            {merchantId}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="left">{orderbuddyMerchantId}</TableCell>
                                                    <TableCell align="left">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color={getMerchantStatusColor(merchantStatus ?? MerchantStatus.Inactive)}
                                                        >
                                                            {merchantStatus ? merchantStatus : 'unknown'}
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell align="left" style={{ display: 'flex' }}>
                                                        {settings?.appServices ? (
                                                            settings.appServices.length > 0 ? (
                                                                [...settings.appServices].sort().map((merchantStatus, index) => (
                                                                    <Button
                                                                        key={index}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="success"
                                                                        style={{ margin: '2px' }}
                                                                    >
                                                                        {merchantStatus || 'unknown'}
                                                                    </Button>
                                                                ))
                                                            ) : (
                                                                <span>No Services</span>
                                                            )
                                                        ) : (
                                                            <span></span>
                                                        )}
                                                    </TableCell>

                                                    {/* <TableCell align="center">
                              <MerchantMoreMenu
                                onDelete={() => handleDeleteMerchant(merchantId)}
                                setIsSwitchMerchant={setIsSwitchMerchant}
                                setMerchantId={setMerchantId}
                                merchantId={merchantId}
                              />
                            </TableCell> */}
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                                {isMerchantNotFound && !isLoading && (
                                    <TableBody>
                                        <TableRow>
                                            <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                                                <SearchNotFound searchQuery={searchValue} isNoItem={isNoItem} />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    </Scrollbar>

                    <TablePagination
                        rowsPerPageOptions={[50, 100]}
                        component="div"
                        count={filteredMerchantList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, page) => setPage(page)}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Card>
            </Container>
            <ConfirmDialog
                title={t('Switch')}
                message={t('Are you sure to switch merchant')}
                open={isSwitchMerchant}
                close={() => setIsSwitchMerchant(false)}
                action={handleSwitchMerchant}
            />
        </Page>
    );
}
